import React, { useEffect, useRef } from "react";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const PRIO_COLOUR = { Urgent: "#e11d48", High: "#f59e0b", Medium: "#6366f1", Low: "#64748b" };

let scriptPromise = null;
function loadMaps() {
  if (window.google && window.google.maps && window.google.maps.drawing) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=drawing,geometry`;
    s.async = true; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function WorkBankMap({ points = [], selectedIds, onSelect, onOpen }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const clustererRef = useRef(null);
  const markersRef = useRef({});
  const drawingRef = useRef(null);
  const selRef = useRef(selectedIds);
  selRef.current = selectedIds;

  useEffect(() => {
    let cancelled = false;
    if (!MAPS_KEY) return;
    loadMaps().then(() => {
      if (cancelled || !ref.current) return;
      const g = window.google.maps;
      const map = new g.Map(ref.current, {
        center: { lat: 53.4, lng: -2.5 }, zoom: 6, mapTypeControl: false, streetViewControl: false,
      });
      mapRef.current = map;
      const dm = new g.drawing.DrawingManager({
        drawingControl: true,
        drawingControlOptions: { position: g.ControlPosition.TOP_LEFT, drawingModes: ["polygon", "circle"] },
        polygonOptions: { fillColor: "#6366f1", fillOpacity: 0.12, strokeColor: "#4f46e5", strokeWeight: 2 },
        circleOptions: { fillColor: "#6366f1", fillOpacity: 0.12, strokeColor: "#4f46e5", strokeWeight: 2 },
      });
      dm.setMap(map);
      drawingRef.current = dm;
      let overlay = null;
      g.event.addListener(dm, "overlaycomplete", (e) => {
        if (overlay) overlay.setMap(null);
        overlay = e.overlay;
        dm.setDrawingMode(null);
        const inside = [];
        for (const p of points) {
          const pos = new g.LatLng(p.lat, p.lng);
          let hit = false;
          if (e.type === "circle") hit = g.geometry.spherical.computeDistanceBetween(pos, e.overlay.getCenter()) <= e.overlay.getRadius();
          else if (e.type === "polygon") hit = g.geometry.poly.containsLocation(pos, e.overlay);
          if (hit) inside.push(p.id);
        }
        onSelect(inside, false);
      });
      renderMarkers();
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  const renderMarkers = () => {
    const g = window.google && window.google.maps;
    if (!g || !mapRef.current) return;
    if (clustererRef.current) clustererRef.current.clearMarkers();
    markersRef.current = {};
    const markers = points.map((p) => {
      const selected = selRef.current?.has(p.id);
      const m = new g.Marker({
        position: { lat: p.lat, lng: p.lng },
        icon: {
          path: g.SymbolPath.CIRCLE, scale: selected ? 8 : 6,
          fillColor: selected ? "#10b981" : (PRIO_COLOUR[p.priority] || "#6366f1"),
          fillOpacity: 1, strokeColor: "#fff", strokeWeight: selected ? 2 : 1,
        },
        title: `${p.work_id} · ${p.title || ""}`,
      });
      const info = new g.InfoWindow({
        content: `<div style="font:13px system-ui;min-width:180px">
          <div style="font-weight:700">${p.work_id}</div>
          <div>${p.customer_name || p.title || ""}</div>
          <div style="color:#64748b">${p.work_type || "—"} · ${p.priority || ""}</div>
          <div style="color:#64748b">Due ${p.deadline_date || "—"} · ${p.estimated_minutes || 0} min</div>
          <button id="wb-open-${p.id}" style="margin-top:6px;background:#4f46e5;color:#fff;border:none;border-radius:6px;padding:4px 10px;cursor:pointer">Open</button>
          </div>`,
      });
      m.addListener("click", () => {
        info.open(mapRef.current, m);
        setTimeout(() => {
          const b = document.getElementById(`wb-open-${p.id}`);
          if (b) b.onclick = () => onOpen(p.id);
        }, 60);
      });
      markersRef.current[p.id] = m;
      return m;
    });
    clustererRef.current = new MarkerClusterer({ map: mapRef.current, markers });
  };

  useEffect(() => { renderMarkers(); /* eslint-disable-next-line */ }, [points, selectedIds]);

  if (!MAPS_KEY) return <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="wb-map-nokey">Google Maps key not configured.</div>;
  return <div ref={ref} className="w-full h-full rounded-xl" data-testid="wb-map" />;
}
